import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil } from "lucide-react";

const EMPTY = {
  title: "", slug: "", excerpt: "", cover_image: "https://images.pexels.com/photos/38696179/pexels-photo-38696179.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1",
  body: "", author: "Bilgece", category: "Bilgelik", tags: [], content_type: "makale",
  seo_title: "", seo_description: "", status: "draft", featured: false,
};
const CATEGORIES = ["Eğitim", "Ebeveynlik", "Öğretmenlik", "Liderlik", "Matematik", "Yapay Zekâ", "Bilgelik", "Yaşam"];
const TYPES = ["makale", "Bilgece Soru", "video", "podcast", "kitap notu", "araştırma", "uygulama", "Bilgece Hikâye"];

export default function ContentManager() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/admin/content").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const uploadCover = async (file) => {
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing((e) => ({ ...e, cover_image: data.url }));
      toast.success("Görsel yüklendi.");
    } catch (err) {
      toast.error("Görsel yüklenemedi.");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    const payload = { ...editing, tags: typeof editing.tags === "string" ? editing.tags.split(",").map((t) => t.trim()).filter(Boolean) : editing.tags };
    try {
      if (editing.id) await api.put(`/admin/content/${editing.id}`, payload);
      else await api.post("/admin/content", payload);
      toast.success("İçerik kaydedildi.");
      setEditing(null); load();
    } catch (e) { toast.error("Kayıt başarısız."); }
  };

  const togglePublish = async (it) => {
    const status = it.status === "published" ? "draft" : "published";
    await api.put(`/admin/content/${it.id}`, { ...it, status, tags: it.tags });
    toast.success(status === "published" ? "Yayınlandı." : "Taslağa alındı.");
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Silinsin mi?")) return;
    await api.delete(`/admin/content/${id}`);
    toast.success("Silindi."); load();
  };

  return (
    <div data-testid="admin-content">
      <div className="flex justify-between items-center mb-6">
        <h1 className="font-serif text-4xl text-brand-green">İçerik Yönetimi</h1>
        <button onClick={() => setEditing({ ...EMPTY })} data-testid="content-new"
          className="inline-flex items-center gap-2 bg-brand-gold text-brand-offwhite px-5 py-2.5 rounded-sm text-sm"><Plus size={16} /> Yeni İçerik</button>
      </div>

      <div className="bg-brand-offwhite border border-brand-gold-light/40 rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-cream text-brand-green text-left"><tr>
            <th className="p-3 font-medium">Başlık</th><th className="p-3 font-medium">Kategori</th>
            <th className="p-3 font-medium">Tür</th><th className="p-3 font-medium">Durum</th><th className="p-3 font-medium">İşlem</th>
          </tr></thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} data-testid={`content-row-${it.id}`} className="border-t border-brand-gold-light/30">
                <td className="p-3 font-medium text-brand-green">{it.title}{it.featured && <span className="ml-2 text-xs text-brand-gold">★</span>}</td>
                <td className="p-3 text-brand-text/70">{it.category}</td>
                <td className="p-3 text-brand-text/70">{it.content_type}</td>
                <td className="p-3"><button onClick={() => togglePublish(it)} data-testid={`content-toggle-${it.id}`}
                  className={`text-xs px-2 py-1 rounded-full ${it.status === "published" ? "bg-brand-green text-brand-offwhite" : "bg-brand-gold-light text-brand-green"}`}>{it.status}</button></td>
                <td className="p-3 flex gap-3">
                  <button onClick={() => setEditing({ ...it, tags: (it.tags || []).join(", ") })} data-testid={`content-edit-${it.id}`} className="text-brand-green"><Pencil size={16} /></button>
                  <button onClick={() => del(it.id)} data-testid={`content-delete-${it.id}`} className="text-red-700"><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-brand-green/50 flex items-start justify-center z-50 p-6 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="bg-brand-offwhite rounded-sm p-8 max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-2xl text-brand-green mb-5">{editing.id ? "İçeriği Düzenle" : "Yeni İçerik"}</h3>
            <div className="space-y-4">
              <Field label="Başlık"><input data-testid="content-field-title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className={inp} /></Field>
              <Field label="Özet"><textarea value={editing.excerpt} onChange={(e) => setEditing({ ...editing, excerpt: e.target.value })} className={inp} rows={2} /></Field>
              <Field label="Kapak Görseli">
                <input value={editing.cover_image} onChange={(e) => setEditing({ ...editing, cover_image: e.target.value })} className={inp} placeholder="URL yapıştır veya dosyadan yükle" />
                <div className="flex items-center gap-3 mt-2">
                  <label className="cursor-pointer inline-flex items-center gap-2 text-sm bg-brand-cream border border-brand-gold-light/50 text-brand-green px-3 py-1.5 rounded-sm hover:border-brand-gold">
                    {uploading ? "Yükleniyor..." : "Dosyadan Yükle"}
                    <input type="file" accept="image/*" className="hidden" data-testid="content-cover-upload"
                      onChange={(e) => e.target.files[0] && uploadCover(e.target.files[0])} />
                  </label>
                  {editing.cover_image && <img src={editing.cover_image} alt="önizleme" className="h-10 w-16 object-cover rounded-sm border border-brand-gold-light/40" />}
                </div>
              </Field>
              <Field label="İçerik (Metin)"><textarea data-testid="content-field-body" value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} className={inp} rows={6} /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Kategori"><select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className={inp}>{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select></Field>
                <Field label="Tür"><select value={editing.content_type} onChange={(e) => setEditing({ ...editing, content_type: e.target.value })} className={inp}>{TYPES.map((t) => <option key={t}>{t}</option>)}</select></Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Yazar"><input value={editing.author} onChange={(e) => setEditing({ ...editing, author: e.target.value })} className={inp} /></Field>
                <Field label="Etiketler (virgülle)"><input value={editing.tags} onChange={(e) => setEditing({ ...editing, tags: e.target.value })} className={inp} /></Field>
              </div>
              <Field label="SEO Başlık"><input value={editing.seo_title} onChange={(e) => setEditing({ ...editing, seo_title: e.target.value })} className={inp} /></Field>
              <Field label="SEO Açıklama"><input value={editing.seo_description} onChange={(e) => setEditing({ ...editing, seo_description: e.target.value })} className={inp} /></Field>
              <div className="flex gap-6 items-center">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} className="accent-brand-gold" /> Öne çıkar</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.status === "published"} onChange={(e) => setEditing({ ...editing, status: e.target.checked ? "published" : "draft" })} className="accent-brand-gold" /> Yayında</label>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={save} data-testid="content-save" className="bg-brand-gold text-brand-offwhite px-6 py-2.5 rounded-sm text-sm">Kaydet</button>
              <button onClick={() => setEditing(null)} className="border border-brand-gold-light px-6 py-2.5 rounded-sm text-sm">İptal</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inp = "w-full border border-brand-gold-light/50 rounded px-3 py-2 text-sm bg-white outline-none focus:border-brand-gold";
function Field({ label, children }) {
  return <div><label className="block text-sm font-medium text-brand-text mb-1">{label}</label>{children}</div>;
}
